import { makeCrewClaimWithClaimLineItemsLoader } from '~/utils/routing';
import { CreditResolutionMethod } from '~/utils/schemas';

type CrewContext = Parameters<
  Parameters<typeof makeCrewClaimWithClaimLineItemsLoader>[0]
>[0]['context'];

const defaultGiftCardTextContent =
  'Receive a gift card once your return is approved.';
const defaultRefundTextContent =
  'Receive a refund to your original payment method once your return is approved. Fees may apply.';

export const getDefaultCreditMethod = (
  offeredMethods: ReturnType<typeof getOfferedCreditMethods>,
  creditMethod?: CreditResolutionMethod,
) => {
  const creditMethodOption = offeredMethods.find(
    (method) => method.type === creditMethod,
  );
  if (creditMethodOption) return creditMethodOption;

  const giftCardMethod = offeredMethods.find(
    (method) => method.type === 'Gift_Card',
  );
  if (giftCardMethod) return giftCardMethod;

  const refundMethod = offeredMethods.find(
    (method) => method.type === 'Refund',
  );
  if (refundMethod) return refundMethod;

  return null;
};

type CreditMethod = {
  type: CreditResolutionMethod;
  label: string;
  description: string;
  badge?: string;
};

export const getOfferedCreditMethods = (
  settings: NonNullable<CrewContext['settings']>,
  lineItems: CrewContext['crewClaim']['claimLineItems'],
): CreditMethod[] => {
  // ---- Refund -----
  const { isRefundEnabled } = settings.return;

  const isRefundOffered =
    isRefundEnabled &&
    lineItems.some((li) => li.lineItem.return.refund.isEligible);

  // ---- Gift Card -----
  const { isGiftCardEnabled } = settings.return;

  const isGiftCardOffered =
    isGiftCardEnabled &&
    lineItems.some((li) => li.lineItem.return.giftCard.isEligible);

  return [
    isGiftCardOffered ?
      {
        type: 'Gift_Card' as const,
        label: 'Gift Card',
        description:
          settings.theme.offerGiftCardDetailText ?? defaultGiftCardTextContent,
      }
    : null,
    isRefundOffered ?
      {
        type: 'Refund' as const,
        label: 'Refund',
        description:
          settings.theme.offerRefundDetailText ?? defaultRefundTextContent,
      }
    : null,
  ].filter(Boolean);
};

export function getCreditMethods(
  settings: NonNullable<CrewContext['settings']>,
  claimLineItems: CrewContext['crewClaim']['claimLineItems'],
  creditMethod?: CreditResolutionMethod,
) {
  const offered = getOfferedCreditMethods(settings, claimLineItems);

  return {
    offered,
    selected: getDefaultCreditMethod(offered, creditMethod),
  };
}
